import MultipleCheckboxesModalInput from '@/common/components/base/input/multipleCheckboxesModalInput/MultipleCheckboxesModalInput';
import MainModal from '@/common/components/base/modal/Modal';
import TitleDescription from '@/common/components/base/typography/TitleDescription';
import { getImageTitlesQueryOptions } from '@/common/queries/selections/ImageTitles/getImageTitlesQueryOptions';
import { getVehiclePartsQueryOptions } from '@/common/queries/selections/vehicleParts/getVehiclePartsQueryOptions';
import { useCorrectiveImages } from '@/modules/adjusterReportModules/IncidentParticipants/store/correctiveImages/useCorrectiveImages';
import { BodyCorrectiveImageModalType } from '@/modules/adjusterReportModules/Insurer-information/components/images/correctiveImages/components/bodyInquiredForm/components/modals/BodyCorrectiveImageModal.type';
import { getBodyVehicleInformationOption } from '@/modules/adjusterReportModules/Insurer-information/queries/getBodyVehicleInformation/getBodyVehicleInformationOption';
import { BodyVehicleCorrectiveImageNameType } from '@/modules/adjusterReportModules/Insurer-information/types/Bodyimages';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { Box } from 'styled-system/jsx';

const BodyCorrectiveImageModal: React.FC<BodyCorrectiveImageModalType> = (
  props,
) => {
  const { id } = useParams();
  const methods = useForm<{
    damaged_parts: { label: string; value: string }[];
    documents: { label: string; value: string }[];
    vehicle: { label: string; value: string }[];
  }>();
  const {
    setSelectedDamagedParts,
    setSelectedVehicle,
    setSelectedDocuments,
    selectedDamagedParts,
    selectedVehicle,
    selectedDocuments,
    preFetchedCorrectiveImages,
  } = useCorrectiveImages();

  const { data: vehicleInformationData } = useQuery(
    getBodyVehicleInformationOption({
      id: +id!,
    }),
  );

  const { data: ImageDocumentSelectionTitles } = useQuery({
    ...getImageTitlesQueryOptions({
      file_type: 'body',
      image_type: 'document',
      user_type: 'insurer',
      damaged_entity_type: 'VEHICLE',
    }),
    enabled: props.type === BodyVehicleCorrectiveImageNameType.documents,
  });
  const { data: ImageVehicleSelectionTitles } = useQuery({
    ...getImageTitlesQueryOptions({
      file_type: 'body',
      image_type: 'vehicle',
      user_type: 'insurer',
      damaged_entity_type: 'VEHICLE',
      vehicle_type:
        vehicleInformationData?.vehicle_model.type === 2 ? 'motorcycle' : 'car',
    }),
    enabled: props.type === BodyVehicleCorrectiveImageNameType.vehicle,
  });
  const { data: ImageDamagedPartsTitles } = useQuery({
    ...getVehiclePartsQueryOptions({
      id: null,
      side: null,
      vehicle_type: vehicleInformationData?.vehicle_model.type,
    }),
    enabled: props.type === BodyVehicleCorrectiveImageNameType.damaged_parts,
  });

  const TitleDescriptionMapper = {
    documents: 'مدارک فردی',
    vehicle: 'تصاویر وسیله‌نقلیه',
    damaged_parts: 'نقاط اسیب دیده',
  };

  useEffect(() => {
    methods.reset({
      damaged_parts: selectedDamagedParts?.map((item) => ({
        label: item.name,
        value: item.name,
      })),
      documents: selectedDocuments?.map((item) => ({
        label: item.name,
        value: item.name,
      })),
      vehicle: selectedVehicle?.map((item) => ({
        label: item.name,
        value: item.name,
      })),
    });
  }, [
    selectedDamagedParts,
    selectedVehicle,
    selectedDocuments,
    preFetchedCorrectiveImages,
  ]);

  return (
    <MainModal open={props.open} closeModal={() => props.closeModal()}>
      <TitleDescription>{TitleDescriptionMapper[props.type]}</TitleDescription>
      <FormProvider {...methods}>
        <Box
          h={300}
          overflowY="auto"
          mt={24}
          display={'flex'}
          flexDirection={'column'}
        >
          {props.type === BodyVehicleCorrectiveImageNameType.damaged_parts && (
            <MultipleCheckboxesModalInput
              name="damaged_parts"
              key={'damaged_parts'}
              data={ImageDamagedPartsTitles ?? []}
              hasInput="omit"
              CheckedTheDisabledCheckBoxes={true}
              disabledCheckBoxes={preFetchedCorrectiveImages.map((item) => ({
                label: item.title,
                value: item.title,
              }))}
              outerOpen={
                props.type === BodyVehicleCorrectiveImageNameType.damaged_parts
              }
              onChange={(data) => {
                const selectedDamgeds = data.map((item) => ({
                  name: item.label,
                  type: TitleDescriptionMapper[
                    BodyVehicleCorrectiveImageNameType.damaged_parts
                  ],
                  enNameOrId: ImageDamagedPartsTitles?.find(
                    (singleItem) => singleItem.label === item.label,
                  )?.enNameOrId?.toString()!,
                }));
                setSelectedDamagedParts(selectedDamgeds);
                props.closeModal();
              }}
              defaultValue={selectedDamagedParts.map((item) => ({
                label: item.name,
                value: item.name,
              }))}
            />
          )}
          {props.type === BodyVehicleCorrectiveImageNameType.documents && (
            <MultipleCheckboxesModalInput
              key={'documents'}
              name="documents"
              data={ImageDocumentSelectionTitles ?? []}
              hasInput="omit"
              disabledCheckBoxes={preFetchedCorrectiveImages.map((item) => ({
                label: item.title,
                value: item.title,
              }))}
              CheckedTheDisabledCheckBoxes={true}
              outerOpen={
                props.type === BodyVehicleCorrectiveImageNameType.documents
              }
              onChange={(data) => {
                const selectedDocs = data.map((item) => ({
                  name: item.label,
                  type: TitleDescriptionMapper[
                    BodyVehicleCorrectiveImageNameType.documents
                  ],
                  enNameOrId: ImageDocumentSelectionTitles?.find(
                    (singleItem) => singleItem.label === item.label,
                  )?.enNameOrId!,
                }));
                setSelectedDocuments(selectedDocs);
                props.closeModal();
              }}
              defaultValue={selectedDocuments.map((item) => ({
                label: item.name,
                value: item.name,
              }))}
            />
          )}
          {props.type === BodyVehicleCorrectiveImageNameType.vehicle && (
            <MultipleCheckboxesModalInput
              name="vehicle"
              key={'vehicle'}
              data={ImageVehicleSelectionTitles ?? []}
              hasInput="omit"
              CheckedTheDisabledCheckBoxes={true}
              disabledCheckBoxes={preFetchedCorrectiveImages.map((item) => ({
                label: item.title,
                value: item.title,
              }))}
              outerOpen={
                props.type === BodyVehicleCorrectiveImageNameType.vehicle
              }
              defaultValue={selectedVehicle.map((item) => ({
                label: item.name,
                value: item.name,
              }))}
              onChange={(data) => {
                const selectedVehicle = data.map((item) => ({
                  name: item.label,
                  type: TitleDescriptionMapper[
                    BodyVehicleCorrectiveImageNameType.vehicle
                  ],
                  enNameOrId: ImageVehicleSelectionTitles?.find(
                    (singleItem) => singleItem.label === item.label,
                  )?.enNameOrId!,
                }));
                setSelectedVehicle(selectedVehicle);
                props.closeModal();
              }}
            />
          )}
        </Box>
      </FormProvider>
    </MainModal>
  );
};
export default BodyCorrectiveImageModal;
